// src/pages/AcceptInvitations.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { unifiedInvitationService } from "@/services/unifiedInvitationService";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle, Building2, MapPin, User } from "lucide-react";
import { AxiosError } from "axios";

interface PendingInvitation {
  _id: string;
  email: string;
  role: string;
  isGlobalInvite: boolean;
  projects: Array<{
    projectId: string;
    projectName: string;
    projectCity?: string;
    projectState?: string;
    projectAddress?: string;
    role: string;
  }>;
  invitedBy: {
    name: string;
    email: string;
  } | null;
  createdAt: string;
  expiresAt: string;
}

const AcceptInvitations = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [acceptingProjects, setAcceptingProjects] = useState<Set<string>>(new Set());

  // Get token from URL query params (if redirected from signup)
  const searchParams = new URLSearchParams(location.search);
  const signupToken = searchParams.get("token");

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  // Get pending invitations
  const {
    data: pendingData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<
    { invitations: PendingInvitation[]; count: number },
    AxiosError<{ error: string }>
  >({
    queryKey: ["pendingInvitations"],
    queryFn: unifiedInvitationService.getPendingInvitations,
    enabled: isAuthenticated,
    staleTime: 1000 * 60, // 1 minute
  });

  // Accept all invitations mutation
  const acceptAllMutation = useMutation({
    mutationFn: unifiedInvitationService.acceptAllInvitations,
    onSuccess: (data) => {
      toast({
        title: "Success",
        description: `Successfully accepted ${data.totalAccepted} invitation(s)`,
        variant: "default",
      });
      
      // Refetch pending invitations and projects
      refetch();
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      
      // Redirect to projects after successful acceptance
      setTimeout(() => {
        navigate("/projects");
      }, 2000);
    },
    onError: (error: AxiosError<{ error: string }>) => {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to accept invitations",
        variant: "destructive",
      });
    },
  });

  // Accept project invitation mutation
  const acceptMutation = useMutation({
    mutationFn: ({ invitationId, projectId }: { invitationId: string; projectId: string }) =>
      unifiedInvitationService.acceptProjectInvitation(invitationId, projectId),
    onMutate: ({ projectId }) => {
      setAcceptingProjects(prev => new Set([...prev, projectId]));
    },
    onSuccess: (data, { projectId }) => {
      toast({
        title: "Success",
        description: data.message || "Project invitation accepted successfully",
        variant: "default",
      });
      
      setAcceptingProjects(prev => {
        const next = new Set(prev);
        next.delete(projectId);
        return next;
      });
      
      // Refetch pending invitations and projects
      refetch();
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (error: AxiosError<{ error: string }>, { projectId }) => {
      toast({
        title: "Error",
        description: error?.response?.data?.error || "Failed to accept invitation",
        variant: "destructive",
      });
      
      setAcceptingProjects(prev => {
        const next = new Set(prev);
        next.delete(projectId);
        return next;
      });
    },
  });

  const handleAcceptAll = () => {
    acceptAllMutation.mutate();
  };

  const handleAcceptProject = (invitationId: string, projectId: string) => {
    acceptMutation.mutate({ invitationId, projectId });
  };

  const handleSkipForNow = () => {
    navigate("/projects");
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <CardTitle>Loading Invitations</CardTitle>
            <CardDescription>
              Please wait while we load your pending invitations...
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center pb-6">
            <Loader2 className="h-8 w-8 animate-spin" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state
  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-2xl border-destructive">
          <CardHeader className="text-center">
            <CardTitle className="text-destructive">Error Loading Invitations</CardTitle>
            <CardDescription>
              {error?.response?.data?.error || error?.message || "Failed to load invitations"}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center pb-6">
            <Button variant="outline" onClick={() => navigate("/projects")}>
              Go to Projects
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const invitations = pendingData?.invitations || [];
  
  // Filter to only show project invitations (exclude global invitations)
  const projectInvitations = invitations.filter(inv => 
    !inv.isGlobalInvite && inv.projects && inv.projects.length > 0
  );

  // If no pending project invitations, redirect to projects
  if (projectInvitations.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <CheckCircle className="h-6 w-6 text-green-500" />
              All Set!
            </CardTitle>
            <CardDescription>
              You don't have any pending project invitations at the moment.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center pb-6">
            <Button onClick={() => navigate("/projects")}>
              Go to Projects
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Project Invitations</h1>
          <p className="text-muted-foreground">
            You have {projectInvitations.length} pending project invitation{projectInvitations.length !== 1 ? 's' : ''}.
            Review and accept the projects you'd like to join.
          </p>
        </div>

        {projectInvitations.length > 1 && (
          <div className="flex justify-center">
            <Button 
              onClick={handleAcceptAll}
              disabled={acceptAllMutation.isPending}
              size="lg"
              className="bg-green-600 hover:bg-green-700"
            >
              {acceptAllMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Accepting All...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Accept All Projects
                </>
              )}
            </Button>
          </div>
        )}

        <div className="space-y-6">
          {projectInvitations.map((invitation) => (
            <Card key={invitation._id} className="w-full">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-xl">
                      {invitation.projects.length} Project{invitation.projects.length !== 1 ? 's' : ''} 
                      {invitation.invitedBy && (
                        <span className="text-lg font-normal text-muted-foreground ml-2">
                          from {invitation.invitedBy.name}
                        </span>
                      )}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        Role: <Badge variant="secondary">{invitation.role}</Badge>
                      </span>
                      {invitation.invitedBy && (
                        <span className="text-sm text-muted-foreground">
                          {invitation.invitedBy.email}
                        </span>
                      )}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-4">
                  {invitation.projects.map((project) => (
                    <div
                      key={project.projectId}
                      className="border rounded-lg p-4 space-y-3"
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-5 w-5 text-muted-foreground" />
                            <h3 className="text-lg font-semibold">{project.projectName}</h3>
                          </div>
                          
                          {(project.projectCity || project.projectState || project.projectAddress) && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <MapPin className="h-4 w-4" />
                              <span>
                                {[project.projectAddress, project.projectCity, project.projectState]
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">
                              Role: {project.role}
                            </Badge>
                          </div>
                        </div>
                        
                        <Button
                          onClick={() => handleAcceptProject(invitation._id, project.projectId)}
                          disabled={acceptingProjects.has(project.projectId)}
                          className="ml-4"
                        >
                          {acceptingProjects.has(project.projectId) ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Accepting...
                            </>
                          ) : (
                            "Accept"
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
              
              <CardFooter className="pt-0">
                <div className="w-full text-center">
                  <p className="text-xs text-muted-foreground">
                    Invitation expires on {new Date(invitation.expiresAt).toLocaleDateString()}
                  </p>
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>

        <div className="flex justify-center space-x-4">
          <Button variant="outline" onClick={handleSkipForNow}>
            Skip for Now
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitations;
